import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { tarifasApi, entidadesApi } from '../services/api';
import { Tag, RefreshCw, Plus, Pencil, Trash2, X, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';

const EMPRESA_COLOR = {
  platecsa: 'bg-blue-100 text-blue-700',
  aguazor: 'bg-emerald-100 text-emerald-700',
};

function fmtPct(n) { return n != null ? `${n}%` : '—'; }

const emptyForm = () => ({
  entidad_id: '',
  empresa: 'platecsa',
  dto1_general: 0,
  condiciones_articulo: [],
  rappel_umbral: '',
  rappel_porcentaje: '',
  notas: '',
  vigente_desde: '',
  vigente_hasta: '',
});

function CondicionesEditor({ condiciones, onChange }) {
  const add = () => onChange([...condiciones, { familia_prefijo: '', dto2: 0 }]);
  const update = (i, field, val) => {
    const next = condiciones.map((c, idx) => idx === i ? { ...c, [field]: val } : c);
    onChange(next);
  };
  const remove = (i) => onChange(condiciones.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      {condiciones.map((c, i) => (
        <div key={i} className="flex gap-2 items-center">
          <input
            placeholder="Familia (ej. G07)"
            value={c.familia_prefijo}
            onChange={e => update(i, 'familia_prefijo', e.target.value.toUpperCase())}
            className="border rounded px-2 py-1 text-sm w-32"
            maxLength={3}
          />
          <input
            type="number"
            step="0.01"
            placeholder="DTO2 %"
            value={c.dto2}
            onChange={e => update(i, 'dto2', parseFloat(e.target.value) || 0)}
            className="border rounded px-2 py-1 text-sm w-24"
          />
          <button onClick={() => remove(i)} className="p-1 text-slate-400 hover:text-red-500">
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add}>
        <Plus className="w-3.5 h-3.5 mr-1" /> Añadir familia
      </Button>
    </div>
  );
}

function ModalTarifa({ tarifa, onClose, onSave }) {
  const [form, setForm] = useState(tarifa ? {
    ...tarifa,
    rappel_umbral: tarifa.rappel_umbral ?? '',
    rappel_porcentaje: tarifa.rappel_porcentaje ?? '',
    vigente_desde: tarifa.vigente_desde ?? '',
    vigente_hasta: tarifa.vigente_hasta ?? '',
  } : emptyForm());
  const [buscandoEntidad, setBuscandoEntidad] = useState('');
  const [sugerenciasEntidad, setSugerenciasEntidad] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [inferido, setInferido] = useState(null);
  const [infiriendo, setInfiriendo] = useState(false);

  const buscarEntidad = useCallback(async (q) => {
    if (!q || q.length < 2) { setSugerenciasEntidad([]); return; }
    try {
      const res = await entidadesApi.list({ q, limit: 5 });
      setSugerenciasEntidad(res.items || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => buscarEntidad(buscandoEntidad), 300);
    return () => clearTimeout(t);
  }, [buscandoEntidad, buscarEntidad]);

  const inferir = async () => {
    if (!form.entidad_id || !form.empresa) { toast.error('Selecciona cliente y empresa'); return; }
    setInfiriendo(true);
    try {
      const res = await tarifasApi.inferir(form.entidad_id, form.empresa);
      setInferido(res);
    } catch (e) {
      toast.error(e.message || 'Error al inferir');
    } finally {
      setInfiriendo(false);
    }
  };

  const aplicarInferido = () => {
    if (!inferido) return;
    setForm(f => ({
      ...f,
      dto1_general: inferido.dto1_sugerido,
      condiciones_articulo: inferido.condiciones_sugeridas,
    }));
    setInferido(null);
    toast.success('Condiciones aplicadas al formulario');
  };

  const guardar = async () => {
    if (!form.entidad_id) { toast.error('Selecciona un cliente'); return; }
    setGuardando(true);
    try {
      const payload = {
        ...form,
        dto1_general: parseFloat(form.dto1_general) || 0,
        rappel_umbral: form.rappel_umbral !== '' ? parseFloat(form.rappel_umbral) : null,
        rappel_porcentaje: form.rappel_porcentaje !== '' ? parseFloat(form.rappel_porcentaje) : null,
        vigente_desde: form.vigente_desde || null,
        vigente_hasta: form.vigente_hasta || null,
      };
      if (tarifa?.id) {
        await tarifasApi.actualizar(tarifa.id, payload);
      } else {
        await tarifasApi.crear(payload);
      }
      toast.success('Tarifa guardada');
      onSave();
      onClose();
    } catch (e) {
      toast.error(e.message || 'Error guardando');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">{tarifa ? 'Editar tarifa' : 'Nueva tarifa'}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-auto flex-1 px-6 py-4 space-y-4">
          {/* Cliente */}
          {!tarifa && (
            <div className="relative">
              <label className="block text-xs font-medium text-slate-600 mb-1">Cliente</label>
              <input
                placeholder="Buscar empresa..."
                value={form._entidad_nombre || buscandoEntidad}
                onChange={e => { setBuscandoEntidad(e.target.value); setForm(f => ({ ...f, _entidad_nombre: e.target.value, entidad_id: '' })); }}
                className="border rounded px-3 py-1.5 text-sm w-full"
              />
              {sugerenciasEntidad.length > 0 && (
                <div className="absolute z-10 bg-white border rounded shadow w-full mt-1">
                  {sugerenciasEntidad.map(e => (
                    <button key={e.id} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                      onClick={() => { setForm(f => ({ ...f, entidad_id: e.id, _entidad_nombre: e.razon_social })); setBuscandoEntidad(''); setSugerenciasEntidad([]); }}>
                      {e.razon_social}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Empresa */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Empresa</label>
            <select value={form.empresa} onChange={e => setForm(f => ({ ...f, empresa: e.target.value }))}
              className="border rounded px-3 py-1.5 text-sm">
              <option value="platecsa">Platecsa</option>
              <option value="aguazor">Aguazor</option>
            </select>
          </div>

          {/* Inferir del histórico */}
          {form.entidad_id && (
            <div className="bg-slate-50 rounded-lg p-3 border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Inferir del histórico</span>
                <Button variant="outline" size="sm" onClick={inferir} disabled={infiriendo}>
                  <Sparkles className="w-3.5 h-3.5 mr-1 text-violet-500" />
                  {infiriendo ? 'Analizando...' : 'Analizar facturas'}
                </Button>
              </div>
              {inferido && (
                <div className="text-sm text-slate-600 space-y-1">
                  <p className="font-medium text-violet-700">
                    Condiciones inferidas de {inferido.num_facturas_analizadas} facturas
                    ({inferido.rango_fechas?.desde} — {inferido.rango_fechas?.hasta})
                  </p>
                  <p>DTO1 sugerido: <strong>{inferido.dto1_sugerido}%</strong></p>
                  <p>{inferido.condiciones_sugeridas.length} condiciones por familia detectadas</p>
                  {inferido.advertencias?.length > 0 && (
                    <p className="text-amber-600">{inferido.advertencias.join(', ')}</p>
                  )}
                  <Button size="sm" onClick={aplicarInferido} className="mt-2">
                    <Check className="w-3.5 h-3.5 mr-1" /> Aplicar al formulario
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* DTO1 */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">DTO1 general (%)</label>
            <input type="number" step="0.01" value={form.dto1_general}
              onChange={e => setForm(f => ({ ...f, dto1_general: e.target.value }))}
              className="border rounded px-3 py-1.5 text-sm w-32" />
          </div>

          {/* Condiciones por familia */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Condiciones por familia de artículo</label>
            <CondicionesEditor
              condiciones={form.condiciones_articulo}
              onChange={v => setForm(f => ({ ...f, condiciones_articulo: v }))}
            />
          </div>

          {/* Rappel */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Rappel umbral (€)</label>
              <input type="number" step="100" value={form.rappel_umbral}
                onChange={e => setForm(f => ({ ...f, rappel_umbral: e.target.value }))}
                className="border rounded px-3 py-1.5 text-sm w-full" placeholder="Sin rappel" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Rappel porcentaje (%)</label>
              <input type="number" step="0.01" value={form.rappel_porcentaje}
                onChange={e => setForm(f => ({ ...f, rappel_porcentaje: e.target.value }))}
                className="border rounded px-3 py-1.5 text-sm w-full" placeholder="%" />
            </div>
          </div>

          {/* Vigencia */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Vigente desde</label>
              <input type="date" value={form.vigente_desde}
                onChange={e => setForm(f => ({ ...f, vigente_desde: e.target.value }))}
                className="border rounded px-3 py-1.5 text-sm w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Vigente hasta (vacío = indefinido)</label>
              <input type="date" value={form.vigente_hasta}
                onChange={e => setForm(f => ({ ...f, vigente_hasta: e.target.value }))}
                className="border rounded px-3 py-1.5 text-sm w-full" />
            </div>
          </div>

          {/* Notas */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notas</label>
            <textarea value={form.notas || ''} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))}
              rows={2} className="border rounded px-3 py-1.5 text-sm w-full resize-none" />
          </div>
        </div>

        <div className="px-6 py-3 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Guardar tarifa'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Tarifas() {
  const [tarifas, setTarifas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empresa, setEmpresa] = useState('');
  const [modal, setModal] = useState(null); // null | 'nueva' | tarifa objeto

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (empresa) params.empresa = empresa;
      const res = await tarifasApi.list(params);
      setTarifas(res.items || []);
    } catch {
      toast.error('Error cargando tarifas');
    } finally {
      setLoading(false);
    }
  }, [empresa]);

  useEffect(() => { cargar(); }, [cargar]);

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta tarifa?')) return;
    try {
      await tarifasApi.eliminar(id);
      toast.success('Tarifa eliminada');
      cargar();
    } catch {
      toast.error('Error eliminando');
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Tag className="w-6 h-6 text-violet-600" />
          <h1 className="text-2xl font-bold text-slate-800">Tarifas</h1>
        </div>
        <div className="flex items-center gap-3">
          <select value={empresa} onChange={e => setEmpresa(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm">
            <option value="">Todas las empresas</option>
            <option value="platecsa">Platecsa</option>
            <option value="aguazor">Aguazor</option>
          </select>
          <Button variant="outline" size="sm" onClick={cargar} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button size="sm" onClick={() => setModal('nueva')}>
            <Plus className="w-4 h-4 mr-1" /> Nueva tarifa
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Cargando tarifas...</div>
        ) : tarifas.length === 0 ? (
          <div className="p-12 text-center">
            <Tag className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-400">No hay tarifas registradas</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left text-slate-500">
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium">Empresa</th>
                <th className="px-4 py-3 font-medium text-right">DTO1</th>
                <th className="px-4 py-3 font-medium text-right">Condiciones</th>
                <th className="px-4 py-3 font-medium text-right">Rappel</th>
                <th className="px-4 py-3 font-medium">Vigente desde</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {tarifas.map(t => (
                <tr key={t.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-800">{t.entidad_id}</td>
                  <td className="px-4 py-3">
                    <Badge className={EMPRESA_COLOR[t.empresa] || 'bg-slate-100 text-slate-600'}>
                      {t.empresa}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{fmtPct(t.dto1_general)}</td>
                  <td className="px-4 py-3 text-right text-slate-500">
                    {(t.condiciones_articulo || []).length} familias
                  </td>
                  <td className="px-4 py-3 text-right text-slate-500">
                    {t.rappel_umbral ? `${t.rappel_umbral.toLocaleString('es-ES')}€ → ${t.rappel_porcentaje}%` : '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{t.vigente_desde || '—'}</td>
                  <td className="px-4 py-3 text-right flex items-center justify-end gap-1">
                    <Button variant="outline" size="sm" onClick={() => setModal(t)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => eliminar(t.id)}
                      className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {modal && (
        <ModalTarifa
          tarifa={modal === 'nueva' ? null : modal}
          onClose={() => setModal(null)}
          onSave={cargar}
        />
      )}
    </div>
  );
}
